// A minimal event emitter for the page-level "events" buses.
//
// Under Vue 2 these buses were `new Vue()` instances handed down as a prop, and
// pages subscribed with `$on` / `$off` / `$once`. Vue 3 removed the instance
// event API, so this is the same contract on a plain object: `on`, `off`,
// `once`, `emit`. It is deliberately NOT reactive — a bus is not state.
export function createEmitter() {
  const listeners = new Map();

  function on(event, handler) {
    if (!listeners.has(event)) listeners.set(event, new Set());
    listeners.get(event).add(handler);
    return () => off(event, handler);
  }

  function off(event, handler) {
    const set = listeners.get(event);
    if (!set) return;
    if (handler) set.delete(handler);
    else set.clear();
  }

  function once(event, handler) {
    const wrapped = (...args) => {
      off(event, wrapped);
      handler(...args);
    };
    return on(event, wrapped);
  }

  function emit(event, ...args) {
    const set = listeners.get(event);
    if (!set) return;
    // Copy first: a handler may unsubscribe itself (once) mid-dispatch.
    for (const handler of Array.from(set)) handler(...args);
  }

  return { on, off, once, emit };
}

export default createEmitter;
